import {useEffect, useMemo, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {PencilIcon, TrashIcon} from '@heroicons/react/24/outline'
import {BackendApi} from '../../../../../api/api.ts'
import {User} from '../../../../../api/accessControl/User.ts'
import {Client} from '../../Client.ts'
import {Input} from '../../../../../components/general/Input.tsx'
import {PrimaryButton} from '../../../../../components/general/PrimaryButton.tsx'
import {SecondaryButton} from '../../../../../components/general/SecondaryButton.tsx'
import Modal from '../../../../../components/general/Modal.tsx'
import {
    deleteGLMapping,
    getGLMapping,
    GLMapping,
    GLMappingRule,
    listGLMappings,
    removeGLMappingRule,
    renameGLMapping,
    upsertGLMappingRule,
} from '../../../../../lib/payrollGLMapping.ts'
import {PayrollGLMappingEditor} from '../../../../../components/specific/PayrollGLMappingEditor'

interface Props {
    api: BackendApi
    user: User
    clients: Client[]
    mappingId?: string
    action?: string
    administrationId?: string
    returnTo?: string
    providerName: string | null
}

type Coverage = {
    allGlAccounts: {code: string; description: string}[]
    unmappedGlAccounts: {code: string; description: string}[]
} | null

type ModalState =
    | {mode: 'closed'}
    | {mode: 'edit'; mapping: GLMapping}

function EditMappingModalContent({
    api,
    user,
    mapping,
    administrationId,
    providerName,
    onMappingRenamed,
}: {
    api: BackendApi
    user: User
    mapping: GLMapping
    administrationId: string | undefined
    providerName: string | null
    onMappingRenamed: (updated: GLMapping) => void
}) {
    const [rules, setRules] = useState<GLMappingRule[] | null>(null)
    const [coverage, setCoverage] = useState<Coverage>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [renameValue, setRenameValue] = useState(mapping.name)
    const [currentMapping, setCurrentMapping] = useState<GLMapping>(mapping)
    const hasLoadedRef = useRef(false)

    useEffect(() => {
        if (hasLoadedRef.current) return
        hasLoadedRef.current = true
        async function load() {
            setIsLoading(true)
            try {
                const loaded = await getGLMapping(api, mapping.mappingId)
                setRules(loaded.rules)
                setCurrentMapping(loaded.mapping)
                if (administrationId) {
                    const cov = await api.Partner__payroll__administrations__mappingCoverage(administrationId)
                    setCoverage({
                        allGlAccounts: cov.all_gl_accounts.map(a => ({code: a.code, description: a.description})),
                        unmappedGlAccounts: cov.unmapped_gl_accounts.map(a => ({code: a.code, description: a.description})),
                    })
                }
            } catch {
                setRules([])
            } finally {
                setIsLoading(false)
            }
        }
        load()
    }, [])

    async function handleRenameBlur() {
        const trimmed = renameValue.trim()
        if (!trimmed || trimmed === currentMapping.name) return
        try {
            const updated = await renameGLMapping(api, mapping.mappingId, trimmed)
            const next = {...currentMapping, name: updated.name}
            setCurrentMapping(next)
            onMappingRenamed(next)
        } catch {
            setRenameValue(currentMapping.name)
        }
    }

    function handleRuleUpserted(rule: GLMappingRule) {
        setRules(prev => {
            if (!prev) return [rule]
            const existing = prev.findIndex(r => r.externalGlAccountCode === rule.externalGlAccountCode)
            if (existing >= 0) {
                const next = [...prev]
                next[existing] = rule
                return next
            }
            return [...prev, rule]
        })
    }

    function handleRuleRemoved(_mappingId: string, code: string) {
        setRules(prev => prev ? prev.filter(r => r.externalGlAccountCode !== code) : prev)
    }

    async function handleUpsertRule(code: string, description: string, glAccountId: string) {
        const rule = await upsertGLMappingRule(api, currentMapping.mappingId, code, description, glAccountId)
        handleRuleUpserted(rule)
    }

    async function handleRemoveRule(code: string) {
        await removeGLMappingRule(api, currentMapping.mappingId, code)
        handleRuleRemoved(currentMapping.mappingId, code)
    }

    return (
        <div className="space-y-5">
            <div className="flex flex-col gap-1.5 max-w-sm">
                <label className="text-sm font-medium text-gray-700">Naam</label>
                <Input
                    value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onBlur={handleRenameBlur}
                    onKeyDown={e => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                        if (e.key === 'Escape') setRenameValue(currentMapping.name)
                    }}
                />
            </div>
            <p className="text-sm text-gray-500">
                Gebruikt door{' '}
                {currentMapping.usedByCount === 1
                    ? '1 klant'
                    : `${currentMapping.usedByCount} klanten`}
            </p>
            {isLoading ? (
                <p className="text-sm text-gray-500 py-2">Laden…</p>
            ) : rules !== null ? (
                <PayrollGLMappingEditor
                    user={user}
                    mapping={currentMapping}
                    rules={rules}
                    coverage={coverage}
                    usedByCount={currentMapping.usedByCount}
                    providerName={providerName}
                    coverageLoadingMessage={null}
                    onUpsertRule={handleUpsertRule}
                    onRemoveRule={handleRemoveRule}
                />
            ) : null}
        </div>
    )
}

export function PayrollTab({api, user, mappingId, action, administrationId, returnTo, providerName}: Props) {
    const navigate = useNavigate()
    const [mappings, setMappings] = useState<GLMapping[]>([])
    const [loadError, setLoadError] = useState<string | null>(null)
    const [modalState, setModalState] = useState<ModalState>({mode: 'closed'})
    const [deleteErrors, setDeleteErrors] = useState<Record<string, string>>({})
    const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set())

    const safeReturnTo = useMemo(() => {
        if (!returnTo) return null
        const decoded = decodeURIComponent(returnTo)
        return decoded.startsWith('/') ? decoded : null
    }, [returnTo])

    async function loadMappings() {
        try {
            const list = await listGLMappings(api)
            setMappings(list)
        } catch {
            setLoadError('Grootboekgekoppelingen konden niet worden geladen.')
        }
    }

    useEffect(() => {
        loadMappings()
    }, [])

    useEffect(() => {
        if (mappingId && action === 'edit' && mappings.length > 0) {
            const target = mappings.find(m => m.mappingId === mappingId)
            if (target) {
                setModalState({mode: 'edit', mapping: target})
            }
        }
    }, [mappings, mappingId, action])

    function handleModalClose(open: boolean) {
        if (open) return
        setModalState({mode: 'closed'})
        if (safeReturnTo) {
            navigate(safeReturnTo)
            return
        }
        loadMappings()
    }

    function handleMappingRenamed(updated: GLMapping) {
        setMappings(prev => prev.map(m => m.mappingId === updated.mappingId ? {...m, name: updated.name} : m))
    }

    async function handleDelete(mapping: GLMapping) {
        if (!window.confirm(`Weet je zeker dat je de koppeling '${mapping.name}' wilt verwijderen?`)) return
        setDeletingIds(prev => new Set(prev).add(mapping.mappingId))
        setDeleteErrors(prev => {
            const next = {...prev}
            delete next[mapping.mappingId]
            return next
        })
        try {
            await deleteGLMapping(api, mapping.mappingId)
            setMappings(prev => prev.filter(m => m.mappingId !== mapping.mappingId))
        } catch (e) {
            const detail = (e as {errorDetail?: {detail?: string}})?.errorDetail?.detail
            setDeleteErrors(prev => ({
                ...prev,
                [mapping.mappingId]: detail ?? 'Deze koppeling is in gebruik en kan niet verwijderd worden.',
            }))
        } finally {
            setDeletingIds(prev => {
                const next = new Set(prev)
                next.delete(mapping.mappingId)
                return next
            })
        }
    }

    const isModalOpen = modalState.mode !== 'closed'
    const modalTitle = modalState.mode === 'edit' ? modalState.mapping.name : ''

    return (
        <div>
            <h3 className="text-xl text-gray-900 font-semibold py-3 pt-6">Loonadministratie</h3>
            <p className="text-sm text-gray-600 mb-6">
                Beheer de grootboekkoppelingen die gebruikt worden bij het importeren van salarissen vanuit je salarispakket.
            </p>

            {loadError && (
                <div className="mb-4 p-3 border border-red-200 rounded-md bg-red-50 text-sm text-red-700">
                    {loadError}
                </div>
            )}

            <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-3 text-left font-medium text-gray-700">Naam</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-700">Gebruikt door</th>
                            <th className="px-4 py-3 text-right font-medium text-gray-700"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                        {mappings.length === 0 && !loadError ? (
                            <tr>
                                <td colSpan={3} className="px-4 py-6 text-center text-sm text-gray-500 italic">
                                    Nog geen koppelingen.
                                </td>
                            </tr>
                        ) : (
                            mappings.map(m => (
                                <tr key={m.mappingId} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3 font-medium text-gray-900">{m.name}</td>
                                    <td className="px-4 py-3 text-gray-500">
                                        {m.usedByCount === 1 ? '1 klant' : `${m.usedByCount} klanten`}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex justify-end items-center gap-2">
                                            <button
                                                type="button"
                                                aria-label="Bewerken"
                                                onClick={() => setModalState({mode: 'edit', mapping: m})}
                                                className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                                            >
                                                <PencilIcon className="h-4 w-4" />
                                            </button>
                                            <button
                                                type="button"
                                                aria-label="Verwijderen"
                                                title={m.usedByCount > 0 ? 'Koppeling is in gebruik' : undefined}
                                                disabled={m.usedByCount > 0 || deletingIds.has(m.mappingId)}
                                                onClick={() => handleDelete(m)}
                                                className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                        {deleteErrors[m.mappingId] && (
                                            <p className="text-xs text-red-600 mt-1 text-right">{deleteErrors[m.mappingId]}</p>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <Modal
                open={isModalOpen}
                updateOpenState={handleModalClose}
                title={modalTitle}
                width="4xl"
            >
                {modalState.mode === 'edit' && (
                    <EditMappingModalContent
                        api={api}
                        user={user}
                        mapping={modalState.mapping}
                        administrationId={mappingId === modalState.mapping.mappingId ? administrationId : undefined}
                        providerName={providerName}
                        onMappingRenamed={handleMappingRenamed}
                    />
                )}

                <div className="mt-8 flex items-center justify-between border-t border-gray-200 pt-5">
                    {safeReturnTo ? (
                        <SecondaryButton title="Terug naar klant" onClick={() => handleModalClose(false)} />
                    ) : (
                        <span />
                    )}
                    <PrimaryButton title="Gereed" onClick={() => handleModalClose(false)} />
                </div>
            </Modal>
        </div>
    )
}
